import React, { useEffect, useCallback } from 'react';
import './AlertModal.css';

/**
 * 커스텀 알림 모달 컴포넌트
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {string} title - 모달 제목 (기본값: 타입에 따라 자동 설정)
 * @param {string} message - 알림 메시지
 * @param {string} confirmText - 확인 버튼 텍스트 (기본: '확인')
 * @param {string} type - 모달 타입: 'success' | 'error' | 'warning' | 'info' (기본: 'info')
 * @param {function} onClose - 닫기 핸들러
 */
const AlertModal = ({
    isOpen,
    title,
    message,
    confirmText = '확인',
    type = 'info',
    onClose
}) => {
    // ESC 키로 모달 닫기
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose?.();
        }
    }, [onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.documentElement.classList.add('alert-modal-open');
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.documentElement.classList.remove('alert-modal-open');
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen) return null;

    // 타입별 기본 제목
    const defaultTitles = {
        success: '완료',
        error: '오류',
        warning: '주의',
        info: '알림'
    };

    // 타입에 따른 아이콘
    const getIcon = () => {
        switch (type) {
            case 'success':
                return (
                    <div className="alert-modal-icon success">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M8 12l2.5 2.5L16 9" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                );
            case 'error':
                return (
                    <div className="alert-modal-icon error">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <rect x="11" y="6" width="2" height="8" rx="1"/>
                            <circle cx="12" cy="17" r="1.5"/>
                        </svg>
                    </div>
                );
            case 'warning':
                return (
                    <div className="alert-modal-icon warning">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2L1 21h22L12 2z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                            <rect x="11" y="9" width="2" height="5" rx="1"/>
                            <circle cx="12" cy="17" r="1.5"/>
                        </svg>
                    </div>
                );
            default:
                return (
                    <div className="alert-modal-icon info">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
                            <circle cx="12" cy="7" r="1.5"/>
                            <rect x="11" y="10" width="2" height="8" rx="1"/>
                        </svg>
                    </div>
                );
        }
    };

    return (
        <div className="alert-modal-overlay" onClick={onClose}>
            <div className="alert-modal" onClick={(e) => e.stopPropagation()}>
                <div className="alert-modal-content">
                    {getIcon()}
                    <h3 className="alert-modal-title">{title || defaultTitles[type]}</h3>
                    <p className="alert-modal-message">{message}</p>
                </div>
                <div className="alert-modal-actions">
                    <button
                        className={`alert-modal-btn confirm ${type}`}
                        onClick={onClose}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AlertModal;
